import React, { useState, useEffect, createContext, useContext } from "react";
import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { 
  Home, FileText, Briefcase, MapPin, Settings, LogOut, Menu, X, ChevronRight, Receipt
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import PortalFABTab from "./PortalFABTab";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const PortalAuthContext = createContext(null);
export const usePortalAuth = () => useContext(PortalAuthContext);

const PortalLayout = () => {
  const [customer, setCustomer] = useState(null);
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const token = localStorage.getItem("portal_token");
    if (!token) { navigate("/portal/login"); return; }
    const fetchData = async () => {
      try {
        const config = { headers: { Authorization: `Bearer ${token}` } };
        // Profile is required, business is optional (for branding)
        const profileRes = await axios.get(`${API}/portal/profile`, config);
        setCustomer(profileRes.data);
        
        // Try to get business info but don't fail if not found
        try {
          const businessRes = await axios.get(`${API}/portal/business`, config);
          setBusiness(businessRes.data);
        } catch (bizErr) {
          // Business info is optional - just log and continue
          console.log("Business info not available:", bizErr.response?.status);
        }
      } catch (err) {
        // Only clear token on 401 (unauthorized) errors
        if (err.response?.status === 401) {
          localStorage.removeItem("portal_token");
          navigate("/portal/login");
        } else {
          console.error("Error loading portal data:", err);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("portal_token");
    toast.success("Logged out successfully");
    navigate("/portal/login");
  };

  const navItems = [
    { path: "/portal", icon: Home, label: "Dashboard", exact: true },
    { path: "/portal/jobs", icon: Briefcase, label: "Jobs" },
    { path: "/portal/invoices", icon: FileText, label: "Invoices" },
    { path: "/portal/payments", icon: Receipt, label: "Payments" },
    { path: "/portal/addresses", icon: MapPin, label: "Addresses" },
    { path: "/portal/settings", icon: Settings, label: "Settings" },
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fb] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#0046BE]/20 border-t-[#0046BE] rounded-full animate-spin" />
      </div>
    );
  }

  const initials = customer?.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'C';

  return (
    <PortalAuthContext.Provider value={{ customer, business, setCustomer }}>
      <div className="min-h-screen bg-[#f8f9fb]" data-testid="portal-layout">
        {/* Header */}
        <header className="bg-white sticky top-0 z-50" style={{boxShadow: '0 1px 3px rgba(0,0,0,0.04)'}}>
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-[60px]">
              <div className="flex items-center gap-3">
                <button className="lg:hidden p-2 -ml-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors" onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="portal-menu-btn">
                  {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
                {business?.logo_url ? (
                  <img src={business.logo_url} alt={business.business_name} className="h-8 object-contain" />
                ) : (
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{background: 'linear-gradient(135deg, #0046BE 0%, #0033A0 100%)'}}>
                      <span className="text-white font-bold text-xs">{business?.business_name?.charAt(0) || 'F'}</span>
                    </div>
                    <span className="text-[15px] font-semibold text-slate-800 font-['Outfit'] hidden sm:block">{business?.business_name || "Customer Portal"}</span>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3">
                <span className="text-sm text-slate-400 hidden sm:block font-['Outfit']">
                  {customer?.name || "Customer"}
                </span>
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold text-white" style={{background: 'linear-gradient(135deg, #0046BE 0%, #0055E6 100%)'}}>
                  {initials}
                </div>
                <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-400 hover:text-slate-600 hover:bg-slate-50 h-8 px-2" data-testid="portal-logout-btn">
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </header>

        <div className="flex max-w-[1400px] mx-auto">
          {/* Desktop Sidebar */}
          <aside className="hidden lg:block w-[240px] flex-shrink-0 pt-6 pr-2 pl-6 sticky top-[60px] h-[calc(100vh-60px)]" data-testid="portal-sidebar">
            <nav className="space-y-0.5">
              {navItems.map((item) => {
                const active = isActive(item.path, item.exact);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`portal-nav-${item.label.toLowerCase()}`}
                    className={`group flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                      active
                        ? "bg-[#0046BE]/[0.06] text-[#0046BE]"
                        : "text-slate-400 hover:text-slate-600 hover:bg-slate-100/60"
                    }`}
                  >
                    <item.icon className={`w-[18px] h-[18px] ${active ? 'text-[#0046BE]' : 'text-slate-300'}`} />
                    <span className="font-['Outfit']">{item.label}</span>
                    {active && <ChevronRight className="w-3.5 h-3.5 ml-auto opacity-40" />}
                  </Link>
                );
              })}
            </nav>
          </aside>

          {/* Mobile Sidebar */}
          {sidebarOpen && (
            <div className="lg:hidden fixed inset-0 z-40" data-testid="portal-mobile-sidebar">
              <div className="fixed inset-0 bg-black/20 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
              <aside className="fixed left-0 top-[60px] bottom-0 w-[260px] bg-white shadow-2xl">
                <nav className="p-4 space-y-0.5">
                  {navItems.map((item) => {
                    const active = isActive(item.path, item.exact);
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setSidebarOpen(false)}
                        className={`flex items-center gap-3 px-3.5 py-3 rounded-xl text-sm font-medium transition-all ${
                          active
                            ? "bg-[#0046BE]/[0.06] text-[#0046BE]"
                            : "text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                        }`}
                      >
                        <item.icon className={`w-[18px] h-[18px] ${active ? 'text-[#0046BE]' : 'text-slate-300'}`} />
                        <span className="font-['Outfit']">{item.label}</span>
                      </Link>
                    );
                  })}
                </nav>
              </aside>
            </div>
          )}

          {/* Main Content */}
          <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8 lg:pl-4" data-testid="portal-content">
            <Outlet />
          </main>
        </div>

        {/* Footer */}
        <footer className="bg-white border-t border-slate-100 py-4 mt-8 pb-20 lg:pb-4">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 text-center text-xs text-slate-300 font-['Outfit']">
            <p>
              {business?.business_name || "Service Provider"} Customer Portal
              {business?.phone && <span className="mx-2 text-slate-200">|</span>}
              {business?.phone && <a href={`tel:${business.phone}`} className="hover:text-[#0046BE] transition-colors">{business.phone}</a>}
            </p>
          </div>
        </footer>

        {/* FAB Tab Bar */}
        <PortalFABTab />
      </div>
    </PortalAuthContext.Provider>
  );
};

export default PortalLayout;
